import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/_components/ui'
import { formatCurrency } from '@/_helpers/currency'
import { formatPercentage } from '@/_helpers/percentage'
import { ContentContainer } from '@/features/_components/content-container'
import { Text } from '@/features/_components/ui/typography'
import { formatDate } from '@/features/_helpers/date'
import type { TExposedApplicationAggregateDTO } from '@doutorsim/shared/applications'

interface ApplicationInfoContentProps {
  application: TExposedApplicationAggregateDTO
}

export const ApplicationInfoContent = ({ application }: ApplicationInfoContentProps) => {
  // The proposal is the authoritative source once the analysis is approved; the
  // simulation snapshot backs the same values from creation until then. Both
  // expose the same loan terms under their own field names.
  const proposal = application.proposal
  const simulation = application.simulation
  const installmentCount = application.installmentCount
  const requestedAmount = application.requestedAmount

  const totalAmountOwed = proposal?.totalAmountOwed ?? simulation?.totalAmountOwed ?? null
  const installmentAmount = proposal?.installmentAmount ?? simulation?.monthlyPaymentAmount ?? null
  const monthlyInterestRate = proposal?.interestRate ?? simulation?.monthlyInterestRate ?? null
  const iofAmount = proposal?.iofAmount ?? simulation?.iofAmount ?? null
  const registerFeeAmount = proposal?.registerFeeAmount ?? simulation?.tacAmount ?? null
  const effectiveInterestRate =
    proposal?.effectiveInterestRate ?? simulation?.monthlyEffectiveInterestRate ?? null

  const totalAmount = totalAmountOwed ?? requestedAmount

  return (
    <div className="flex flex-col gap-5">
      <ContentContainer className="gap-6">
        <h2 className="font-bold">Parcela selecionada pelo paciente</h2>

        {installmentCount && totalAmount ? (
          <div className="text-brand-green-primary-5">
            <p className="text-4xl font-bold">
              {installmentCount}x{' '}
              {formatCurrency(installmentAmount ?? totalAmount / installmentCount)}
            </p>
            <Text className="mt-1 text-base">Total de {formatCurrency(totalAmount)}</Text>
          </div>
        ) : (
          <p className="text-sm font-semibold">Não há parcelas solicitadas no momento.</p>
        )}

        <div>
          <h3 className="text-sm font-bold">Informações do parcelamento</h3>

          <div className="space-y-[5px] text-brand-grayscale-7">
            <div className="flex w-full justify-between">
              <span>Valor solicitado</span>
              <span>{requestedAmount ? formatCurrency(requestedAmount) : '-'}</span>
            </div>
            <div className="flex w-full justify-between">
              <span>Valor total</span>
              <span>{totalAmount ? formatCurrency(totalAmount) : '-'}</span>
            </div>
            <div className="flex w-full justify-between">
              <span>Prazo</span>
              <span>{installmentCount ? `${installmentCount} parcelas` : '-'}</span>
            </div>
            <div className="flex w-full justify-between">
              <span>Primeira Parcela</span>
              <span>
                {application.firstPaymentDate
                  ? formatDate(new Date(application.firstPaymentDate))
                  : '-'}
              </span>
            </div>
            <div className="flex w-full justify-between">
              <span>Taxa de juros mensal</span>
              <span>
                {monthlyInterestRate != null ? formatPercentage(monthlyInterestRate) : '-'}
              </span>
            </div>
          </div>

          <Accordion type="single" collapsible className="mt-3">
            <AccordionItem value="detalhes" className="border-none">
              <AccordionTrigger className="flex-none justify-normal gap-1 p-0 text-sm font-bold text-brand-green-primary-5">
                Ver mais
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="space-y-[5px] text-base text-brand-grayscale-7">
                  <div className="flex w-full justify-between">
                    <span>IOF</span>
                    <span>{iofAmount != null ? formatCurrency(iofAmount) : '-'}</span>
                  </div>
                  <div className="flex w-full justify-between">
                    <span>TC</span>
                    <span>
                      {registerFeeAmount != null ? formatCurrency(registerFeeAmount) : '-'}
                    </span>
                  </div>
                  <div className="flex w-full justify-between">
                    <span>Custo efetivo mensal</span>
                    <span>
                      {effectiveInterestRate != null
                        ? formatPercentage(effectiveInterestRate)
                        : '-'}
                    </span>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ContentContainer>
    </div>
  )
}
